import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, pluck } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CategoryBuilder } from '../../models/builders/category.builder';
import { ManufacturingOrderBuilder } from '../../models/builders/manufacturing-order.builder';
import { MaterialBuilder } from '../../models/builders/material.builder';
import { SchedulerTaskBuilder } from '../../models/builders/scheduler-task.builder';
import { getErrorHandlingOptions } from '../base/headers';
import { ManufacturingOrder } from '../../models/manufacturing-order';
import { Material } from '../../models/material';
import { isArray } from 'util';
import { parseNumber } from '../../utils/number.utils';
import { ManufacturingOrderFileBuilder } from '../../models/builders/manufacturing-order-file.builder';
import { ManufacturingOrderFilesBuilder } from '../../models/builders/manufacturing-order-files.builder';
import { ManufacturingOrderFile } from '../../models/manufacturing-order-file';
import { ManufacturingOrderWithTaskBuilder } from '../../models/builders/manufacturing-order-with-task.builder';

const saveMaterialsBody = (materials: Material[]) => {
  return (isArray(materials))
    ? materials.map(material => ({
      hash: material.hash,
      fileName: material.fileName,
      code: material.code,
      amount: material.amount,
      planeNumber: material.planeNumber,
      denomination: material.denomination,
      reference: material.reference,
      category: material.category,
      subcategory: material.subcategory,
      printNumber: material.printNumber,
      addByHand: material.addByHand
    }))
    : [];
};

const saveManufacturingOrderBody = (manufacturingOrder: ManufacturingOrder) => {
  return {
    code: manufacturingOrder.code,
    subcustomer: manufacturingOrder.subCustomer,
    reference: manufacturingOrder.reference,
    project: manufacturingOrder.project,
    projectNumber: manufacturingOrder.projectNumber,
    manufacturingOrderFeature: manufacturingOrder.manufacturingOrderFeature,
    materials: saveMaterialsBody(manufacturingOrder.materials)
  };
};

@Injectable()
export class ManufacturingOrderApiService {

  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${ environment.apiUrl }manufacturing-orders`;
  }

  validateDelete(order: string) {
    return this.httpClient.delete(`${ this.apiUrl }/validate-delete/${ order }`)
      .pipe(
        map(SchedulerTaskBuilder.fromList)
      );
  }

  delete(order: string) {
    return this.httpClient.delete(`${ this.apiUrl }/${ order }`);
  }

  getManufacturingOrders() {
    return this.httpClient.get(this.apiUrl)
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  getManufacturingOrdersByModel(model: string) {
    return this.httpClient.get(`${this.apiUrl}/${model}`)
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  getManufacturingOrdersNotScheduled() {
    return this.httpClient.get(`${this.apiUrl}/not-scheduled`)
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  getManufacturingOrdersNotScheduledByModel(model: string) {
    return this.httpClient.get(`${this.apiUrl}/not-scheduled/${model}`)
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  // TODO remove
  getManufacturingOrdersFilteredByCode() {
    return this.httpClient.get(`${ this.apiUrl }/filteredByCode`)
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  getManufacturingOrder(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
      .pipe(
        map(ManufacturingOrderBuilder.fromJson)
      );
  }

  getCategoriesFromOrder(manufacturingOrder: string) {
    return this.httpClient.get(`${ this.apiUrl }/${ manufacturingOrder }/categories`, getErrorHandlingOptions())
      .pipe(
        map(CategoryBuilder.fromList)
      );
  }

  getMaterialsFromOrderAndCategory(manufacturingOrder: string, category: string) {
    return this.httpClient.get(`${ this.apiUrl }/${ manufacturingOrder }/${ category }/materials`, getErrorHandlingOptions())
      .pipe(
        map(MaterialBuilder.fromList)
      );
  }

  getFilesFromOrderAndFileCategories(manufacturingOrder: string) {
    return this.httpClient.get(`${ this.apiUrl }/${ manufacturingOrder }/files`, getErrorHandlingOptions())
      .pipe(
        map(ManufacturingOrderFileBuilder.fromList)
      );
  }

  createManufacturingOrder(manufacturingOrder: ManufacturingOrder) {
    return this.httpClient.post<{ hash: string, existingFilesNumber: number }>(`${ this.apiUrl }`,
      saveManufacturingOrderBody(manufacturingOrder))
      .pipe(
        // pluck('existingFilesNumber'),
        map((json: any) => ({ hash: json.hash, existingFilesNumber: parseNumber(json.existingFilesNumber) }))
      );
  }

  updateManufacturingOrder(manufacturingOrder: ManufacturingOrder) {
    return this.httpClient.put<{ existingFilesNumber: number }>(`${ this.apiUrl }/${ manufacturingOrder.hash }`,
      saveManufacturingOrderBody(manufacturingOrder))
      .pipe(
        pluck('existingFilesNumber'),
        map(parseNumber)
      );
  }

  savePlannerObservations(hash: string, observations: string) {
    return this.httpClient.put<{ hash: string, observations: string }>(
      `${ this.apiUrl }/${ hash }/observations`,
      { observations }
    );
  }

  // Bloquea/desbloquea la OF (VCG): queda en el histórico y, bloqueada, no se puede enviar a máquina.
  blockManufacturingOrder(
    hash: string,
    blocked: boolean,
    receivers: { hash: string, isRole: boolean, isUser: boolean }[],
    comment: string
  ) {
    return this.httpClient.put<{ hash: string, blocked: boolean }>(
      `${ this.apiUrl }/${ hash }/block`,
      { blocked, receivers, comment }
    );
  }

  syncFilesFromOrder(manufacturingOrder: string) {
    return this.httpClient.get(`${ this.apiUrl }/${ manufacturingOrder }/sync-files`, getErrorHandlingOptions())
      .pipe(
        map(ManufacturingOrderFilesBuilder.fromJson)
      );
  }

  deleteFile(file: ManufacturingOrderFile) {
    return this.httpClient.delete(`${ this.apiUrl }/files/${ file.hash }`);
  }

  getSearchRegisteredManufacturingOrders(search: string) {
    const params = new HttpParams().append('search', search);
    return this.httpClient.get(`${ this.apiUrl }/search`, { params })
    .pipe(
      map(ManufacturingOrderBuilder.buildRegisteredManufacturingOrderFromList)
    );
  }

  getSearchRegisteredManufacturingOrdersWithTask(search: string, userActivity: string, code: string = null) {
    let params = new HttpParams()
      .append('search', search)
      .append('userActivity', userActivity);

    if (code) {
      params = params.append('code', code);
    }

    return this.httpClient.get(`${ this.apiUrl }/searchWithTask`, { params })
    .pipe(
      map(ManufacturingOrderWithTaskBuilder.fromList)
    );
  }

  getSearchRegisteredManufacturingOrdersWithTaskByValidation(hash: string) {
    const params = new HttpParams().append('validation', hash);
    return this.httpClient.get(`${ this.apiUrl }/searchWithTaskByValidation`, { params })
    .pipe(
      map(ManufacturingOrderWithTaskBuilder.fromList)
    );
  }

  getSearchRegisteredManufacturingOrdersWithTaskByWorkingHour(hash: string) {
    const params = new HttpParams().append('workingHour', hash);
    return this.httpClient.get(`${ this.apiUrl }/searchWithTaskByWorkingHour`, { params })
    .pipe(
      map(ManufacturingOrderWithTaskBuilder.fromList)
    );
  }

  getRegisteredProjectNumbers(
    manufacturingOrderCode: string,
    searchNc: boolean = true
  ) {
    let params = new HttpParams().append('manufacturingOrderCode', manufacturingOrderCode);
    params = params.append('searchNc', searchNc.toString());

    return this.httpClient.get(`${ this.apiUrl }/projects`, { params })
    .pipe(
      map(ManufacturingOrderBuilder.buildRegisteredProjectNumberFromList)
    );
  }

  getFile(fileUrl: string) {
    return this.httpClient.get(fileUrl, { responseType: 'arraybuffer' });
  }

  getManufacturingOrdersByWorkingHoursValidation(workingHoursValidation: string) {
    const params = new HttpParams().append('workingHoursValidationHash', workingHoursValidation);

    return this.httpClient.get(`${ this.apiUrl }/by-working-hours-validation`, { params })
      .pipe(
        map(ManufacturingOrderBuilder.fromList)
      );
  }

  getSearchManufacturingOrders(search: string) {
    const params = new HttpParams().append('search', search);
    return this.httpClient.get(`${ this.apiUrl }/search`, { params })
    .pipe(
      map(ManufacturingOrderBuilder.buildRegisteredManufacturingOrderFromList)
    );
  }

  generateManufacturingOrderPDF(hash: any) {
    return this.httpClient.post(
      `${this.apiUrl}/${hash}/generate-pdf`, {}, { responseType: 'arraybuffer' }
    );
  }

}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/manufacturing-order-api.service.ts